import { useEffect, useState } from 'react';
import {
  api, baustelle, darf, datum, uhr, zeitpunkt, GEWERK_TEXT, type Anmelder, heuteIso, tagIso, pushFrage} from '../api.ts';
import {
  Auswahl, Dialog, Fehlerkasten, Text, ausFehler, type Fehlerlage,
} from '../teile/Formular.tsx';
import Wochenraster from '../teile/Wochenraster.tsx';
import {
  heuteTag, langTag, montagVon, plusTage, tageAb, wochentag, type Tag,
} from '../teile/rastertage.ts';
import Auftragsinfoliste from '../teile/Auftragsinfoliste.tsx';
import { MitGrund } from './Aufmass.tsx';
import TerminStornoFenster from '../teile/TerminStornoFenster.tsx';
import { Kopf, Karte, Leer } from '../teile/Geruest.tsx';
import { Glasfasertermine } from '../teile/Glasfaser.tsx';
import Nachfragen from '../teile/Nachfragen.tsx';
import Terminkalender, { type Bautag } from '../teile/Terminkalender.tsx';
import { useFrisch } from '../teile/Frisch.tsx';

/* DER MONTAG EINER WOCHE - OHNE UHR.
   ==========================================================================
   ORIGINALTEXT MARVIN (10.09.2026, mit 2 Bildschirmfotos)
     „bild 1: von kamil, deutsche zeitzone, bild 2: mein pc, zypern zeitzone.
      aber das problem sind die wochentage, datum."

   HIER STAND `x.setHours(0, 0, 0, 0)`, also Mitternacht auf der Uhr des
   BETRACHTERS. In Zypern ist das `2026-09-06T21:00:00Z`, und in Berlin - wo
   die Termine gelesen wurden - ist das der 06.09. um 23:00. Unter „Montag
   07.09." standen deshalb die Termine vom Sonntag.

   Die Rechnung steht jetzt in `teile/rastertage.ts` und kennt keine Uhr. */
const montag = montagVon;

/**
 * Ein Datum als ISO, IN ORTSZEIT.
 *
 * NIE `toISOString()`. Das rechnet nach UTC um, und die Tage in diesem Raster
 * stehen auf ORTS-Mitternacht: der 26.08. um 00:00 in Berlin ist der 25.08.
 * um 22:00 in UTC. Damit war der umrandete „heute" den ganzen Sommer über der
 * FOLGETAG. Am 25.08.2026 stand der Rahmen um den Mittwoch.
 *
 * Es ist dieselbe Falle wie in `automatik.ts`, und dort steht sie seit
 * Wochen als Regel da: „tagIso rechnet in Ortszeit, nie toISOString()."
 * Eine Regel, die an einer Stelle steht und an der anderen fehlt, ist keine.
 */
/* `iso()` STAND HIER ALS EIGENE FASSUNG und ist am 09.09.2026 gefallen.
   Sie las den Tag ueber `getFullYear/getMonth/getDate`, also aus der Uhr
   des BETRACHTERS - waehrend die Anzeige daneben fest auf Berlin stand.
   Es gab 6 solcher Fassungen in dieser Oberflaeche. Sie steht jetzt
   EINMAL in `api.ts` als `tagIso` und traegt die Zone selbst. */
const iso = (d: Date) => tagIso(d);

/**
 * Gleichzeitige Termine desselben Auftrags werden EINE Karte.
 *
 * ORIGINALTEXT MARVIN (25.08.2026)
 *   „der terminplaner/wochenplan soll wie sonst auch die EB/SP termine, wenn
 *    sie zur gleichen zeit sind zusammenfassen."
 *
 * Einblasen und Spleissen laufen fast immer am selben Haus zur selben Zeit
 * durch dieselbe Firma: die Montagekopplung. Zwei Karten untereinander mit
 * derselben Uhrzeit, derselben Adresse und derselben Firma sind deshalb keine
 * zwei Aufgaben, sondern eine, und der Tag sieht doppelt so voll aus wie er
 * ist.
 *
 * ZUSAMMENGEFASST WIRD NUR, WAS WIRKLICH DASSELBE IST: derselbe Auftrag,
 * derselbe Tag, dieselbe Uhrzeit, dieselbe Firma. Zwei Gewerke am selben Haus
 * zu VERSCHIEDENEN Zeiten bleiben zwei Karten, sonst verschwindet ein Termin
 * hinter dem anderen.
 *
 * UND NUR EINBLASEN MIT SPLEISSEN. Der Tiefbau kommt mit Bagger und anderer
 * Mannschaft; er gehoert nie in dieselbe Karte, auch wenn er zufaellig
 * dieselbe Uhrzeit traegt. Beim ersten Entwurf hat genau das zugeschlagen:
 * ein noch nicht vergebener Tiefbau und ein noch nicht vergebenes Spleissen
 * hatten beide keine Firma und keine Uhrzeit und wurden zu „TB+SP".
 */
const MONTAGE = ['EB', 'SP'];

function zusammenfassen(liste: any[]): any[] {
  const raus: any[] = [];
  const nach = new Map<string, any>();
  for (const x of liste) {
    // Was nicht zur Montage gehoert, bekommt einen Schluessel, den kein
    // zweiter Termin trifft: es bleibt fuer sich.
    const schluessel = MONTAGE.includes(x.art)
      ? ['montage', x.auftrag_id, x.termin_datum, x.termin_von ?? '',
         x.firma ?? ''].join('|')
      /* DER TAG GEHÖRT IN DEN SCHLÜSSEL, seit ein mehrtägiger Tiefbau an
         JEDEM seiner Tage im Plan steht. Ohne ihn träfen der erste und der
         dritte Tag denselben Schlüssel (die Kennung des Gewerks ist dieselbe),
         und aus drei Tagen würde wieder einer - genau der Zustand, den die
         Folgetage abschaffen sollen. Zusammengefasst wird HIER je Tag, also
         reicht er auch aus. */
      : 'einzeln|' + x.id + '|' + (x.termin_datum ?? '');
    const da = nach.get(schluessel);
    if (!da) {
      const neu = { ...x, arten: [x.art], ids: [x.id] };
      nach.set(schluessel, neu);
      raus.push(neu);
      continue;
    }
    da.arten.push(x.art);
    da.ids.push(x.id);
    // Erledigt ist die Zusammenfassung nur, wenn ALLE erledigt sind. Ein
    // Haken über einem offenen Gewerk waere eine falsche Auskunft.
    da.erledigt = da.erledigt && x.erledigt;
  }
  // Die Reihenfolge der Gewerke ist fest, nicht die des Zufalls: TB, EB, SP.
  const rang: Record<string, number> = { TB: 1, EB: 2, SP: 3, DK: 4 };
  for (const x of raus) {
    x.arten.sort((a: string, b: string) => (rang[a] ?? 9) - (rang[b] ?? 9));
  }
  return raus;
}

/**
 * Ein Termin im Wochenplan, angeklickt.
 *
 * ORIGINALTEXT MARVIN (25.08.2026)
 *   „wenn ich auf einen eintrag klicke dann bitte nicht raus springen aus dem
 *    auftrag, sondern als popup öffnen. zudem fehlt die vollständige adresse."
 *
 * DER KLICK HAT DEN BILDSCHIRM GEWECHSELT, und das war die falsche Antwort
 * auf die falsche Frage. Wer im Wochenplan auf einen Termin zeigt, will
 * meistens EINE Auskunft: wo genau ist das, wer fährt hin, wann. Dafür den
 * ganzen Plan zu verlassen heisst: nachsehen, zurückgehen, die Woche wieder
 * suchen, die Stelle wiederfinden. Bei zehn Terminen zehnmal.
 *
 * Also ein Fenster darüber. Der Weg in die Akte bleibt - als Knopf, für den,
 * der wirklich dorthin will.
 *
 * UND DIE GANZE ANSCHRIFT. In der Kachel steht aus Platzgründen Strasse und
 * Nummer; hier steht, was man in ein Navigationsgerät tippt, samt Gemeinde -
 * die Behörde, bei der die Anordnung liegt.
 */
function Terminfenster(
  { x, ich, zu, zurAkte, neuLaden }:
  { x: any; ich: Anmelder; zu: () => void; zurAkte: () => void;
    neuLaden: () => void }
) {
  /* WAS MAN HIER TUN KANN, STEHT HIER.
   *
   * ORIGINALTEXT MARVIN (26.08.2026)
   *   „wenn ich dort auf einen auftrag klicke muss ich ja auch direkt auf
   *    bild 3 die wichtigen möglichkeiten haben, wie zb termin umbuchen,
   *    stornieren."
   *
   * BEIDE WEGE GAB ES, UND BEIDE LAGEN IN DER AUFTRAGSAKTE. Wer im Wochenplan
   * merkt, dass ein Termin verschoben werden muss, hat den Grund vor Augen -
   * und musste dafür den Plan verlassen, die Akte suchen, das Terminfenster
   * öffnen, die Zeile finden. Bei acht Verschiebungen achtmal. Der Weg in die
   * Akte bleibt, für den, der wirklich dorthin will.
   */
  const [was, setWas] = useState<null | 'umbuchen' | 'storno' | 'oeffnen'>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  /* DIE KOMPLETTE ANSCHRIFT MIT KLS UND SM, an EINER Stelle gerechnet.
     Marvin, 03.09.2026: „ich will die komplette adresse stehen haben, auch
     mit KLS und SM ... ich habe eh platz im titel." */
  const anschrift = baustelle(x);

  /* NACH DEM STORNO: DEN KUNDEN FRAGEN (.354). Marvin, 13.10.2026: „Ja baue
     es ein." Der Server sagt in `nachfrage`, was zu fragen ist. */
  const [absage, setAbsage] = useState<any>(null);
  if (absage) {
    return (
      <Nachfragen auftragId={absage.auftragId ?? x.auftrag_id} nachfrage={absage}
                  baustelle={anschrift}
                  fertig={() => { setAbsage(null); neuLaden(); zu(); }} />
    );
  }

  const darfVergeben = darf(ich, 'termin.vergeben');

  if (was === 'storno') {
    return (
      <TerminStornoFenster
        titel={`Termin ${anschrift} stornieren`}
        auftragId={String(x.auftrag_id)} arten={x.arten ?? [x.art]}
        erklaerung="Der Termin fällt aus der Planung. Der Grund gehört in die
                    Akte: bei der nächsten Vergabe will man wissen, warum."
        laeuft={laeuft} fehler={fehler} zu={() => setWas(null)}
        fertig={async (grund, gbgs) => {
          setLaeuft(true); setFehler(null);
          try {
            /* ALLE GEWERKE DIESER KARTE, IN EINER ANFRAGE.
               ==========================================================
               ORIGINALTEXT MARVIN (22.09.2026)
                 „ich habe dir doch schon mal gesagt, überall wo einblasen
                  und spleißen auf den gleichen termin liegen schickst du
                  eine gesammelte meldung raus!"

               Hier stand eine SCHLEIFE über `x.ids`, eine Anfrage je Gewerk.
               Die Bündelung dahinter gibt es seit .132 - sie sah aber je
               Anfrage nur EIN Gewerk, und aus einem lässt sich nichts
               bündeln. Auf Marvins Bild standen deshalb 2 Absagen um 12:34
               untereinander, beide Schleusenweg 10, beide 23.09. um 08:30.

               **EINE REGEL, DIE ÜBER MEHRERE ZEILEN ENTSCHEIDET, MUSS ALLE
               ZEILEN AUF EINMAL SEHEN.** Einblasen und Spleissen stehen als
               EINE Karte da, weil sie dieselbe Fahrt sind; sie gehen jetzt
               auch als eine Anfrage hinaus. */
            /* ERST FRAGEN, DANN PUSHEN (.358). */
            const push = await pushFrage(x.termin_datum);
            const r = await api.terminStorno(x.ids[0], grund, x.ids.slice(1), push, gbgs);
            if (r?.nachfrage) { setAbsage(r.nachfrage); return; }
            neuLaden(); zu();
          } catch (f) { setFehler(ausFehler(f)); }
          finally { setLaeuft(false); }
        }}
      />
    );
  }

  /* WIEDER OEFFNEN, MIT GRUND. Er ist Pflicht, und zwar aus demselben Grund
     wie beim Stornieren: bei der naechsten Vergabe will man wissen, warum
     noch einmal hingefahren wird. Der Grund geht in die Akte, nicht nur ins
     Protokoll. */
  if (was === 'oeffnen') {
    return (
      <MitGrund
        titel={`${anschrift} wieder öffnen`} knopf="Wieder öffnen"
        erklaerung="Der Haken „erledigt“ fällt, der Termin bleibt stehen. Danach
                    lässt sich umbuchen. Was bisher an diesem Gewerk steht -
                    Aufmaß, Dateien, Kommentare - bleibt unangetastet."
        laeuft={laeuft} fehler={fehler} zu={() => setWas(null)}
        fertig={async (grund) => {
          setLaeuft(true); setFehler(null);
          try {
            /* ALLE GEWERKE DIESER KARTE, wie beim Stornieren daneben: Einblasen
               und Spleissen sind EINE Fahrt. Eines zu oeffnen und das andere
               abgehakt zu lassen waere das Gegenteil dessen, was die Karte
               sagt. */
            for (const id of x.ids) {
              await api.terminErledigt(id, { erledigt: false, grund });
            }
            neuLaden(); zu();
          } catch (f) { setFehler(ausFehler(f)); }
          finally { setLaeuft(false); }
        }}
      />
    );
  }

  if (was === 'umbuchen') {
    return (
      <Umbuchen x={x} anschrift={anschrift} zu={() => setWas(null)}
                fertig={() => { neuLaden(); zu(); }} />
    );
  }

  return (
    /* BREIT GENUG FÜR DIE AUFTRAGSINFO (.279). ORIGINALTEXT MARVIN
       (02.10.2026): „hier ist das fenster zu klein, das muss breiter sein."
       Mit 560 rollte das Fenster quer und die langen Zeilen der
       Auftragsinfo liefen hinten heraus. Der Dialog deckelt selbst bei
       96vw, auf dem Telefon bleibt es also bildschirmbreit. */
    <Dialog titel={anschrift} zu={zu} breite={960}>
      <dl className="felder">
        <dt>Gewerk</dt>
        <dd>
          {x.arten.map((a: string) => GEWERK_TEXT[a]).join(' und ')}
          {x.erledigt && <span className="merker gruen">erledigt</span>}
          {x.restarbeiten_offen && (
            <span className="merker warnung">Restarbeiten offen</span>
          )}
        </dd>

        <dt>Wann</dt>
        <dd>
          {datum(x.termin_datum)}
          {x.termin_von && <>, {uhr(x.termin_von)}</>}
          {x.termin_bis && <> bis {uhr(x.termin_bis)}</>}
          {x.fenster === 'ausnahme' && (
            <div className="neben">Randzeit, ausdrücklich vereinbart</div>
          )}
        </dd>

        <dt>Firma</dt>
        <dd>
          {x.firma ?? <span className="leise">noch nicht vergeben</span>}
          {x.eigenpersonal && <div className="neben">eigenes Personal</div>}
        </dd>

        {/* DIE ANSCHRIFT NOCH EINMAL ALS FELD, obwohl sie im Titel steht.
            Aus dem Titel kopiert man nicht, und der Weg dorthin gehört
            daneben: wer das Fenster öffnet, steht oft schon halb im Auto. */}
        <dt>Anschrift</dt>
        <dd>
          {x.strasse} {x.hausnummer}{x.hausnummer_zusatz ?? ''}
          <div>{x.plz} {x.ort_text}</div>
          <div className="neben">
            <a href={'https://www.google.com/maps/dir/?api=1&destination='
                     + encodeURIComponent(anschrift)}
               target="_blank" rel="noreferrer">Navigation</a>
          </div>
        </dd>

        <dt>Gemeinde</dt>
        <dd>{x.gemeinde ?? <span className="leise">nicht zugeordnet</span>}</dd>

        {!ich.istSub && (
          <>
            <dt>KLS-ID</dt>
            <dd className="kennung">
              {x.kls_id ?? <span className="leise">fehlt</span>}
            </dd>
            <dt>Kunde</dt>
            <dd>{x.kunde_name ?? <span className="leise">nicht erfasst</span>}</dd>
          </>
        )}
      </dl>

      {/* DIE AUFTRAGSINFO GEHÖRT HIERHER, UND EIN SCHREIBFELD DAZU.
          ORIGINALTEXT MARVIN (26.08.2026)
            „wenn ich dann das popup öffne brauche ich die auftragsinfo mit
             kommentaren, bzw das ich dann auch was reinschreiben kann. diese
             ansicht hier bringt mich nicht weiter um zu reagieren."

          ER HAT RECHT: DAS FENSTER WAR EIN STECKBRIEF. Adresse, Gewerk,
          Firma, KLS - lauter Angaben, die man nachschlägt und nicht
          beantwortet. Wer im Wochenplan auf einen Termin zeigt, will
          meistens REAGIEREN: der Kunde hat angerufen, die Firma kommt nicht,
          der Bagger steht woanders. Dafür braucht er, was zuletzt passiert
          ist, und einen Platz für den Satz, den er gerade sagen will. */}
      <Verlauf auftragId={x.auftrag_id} ich={ich}
               gewerk={x.arten.length === 1 ? x.arten[0] : undefined} />

      {/* KEIN ZWEITES „SCHLIESSEN".
          Marvin, 26.08.2026: „2x schließen braucht man auch nirgendswo haben.
          am besten immer oben rechts." Der Knopf oben rechts schliesst, die
          Escape-Taste schliesst, der Klick daneben schliesst. Ein vierter Weg
          in der Knopfreihe verdrängt nur die Knöpfe, die etwas TUN. */}
      <div className="reihe" style={{ marginTop: 14 }}>
        {darfVergeben && !x.erledigt && (
          <>
            <button className="knopf haupt" onClick={() => setWas('umbuchen')}>
              Termin umbuchen
            </button>
            <button className="knopf" onClick={() => setWas('storno')}>
              Stornieren
            </button>
          </>
        )}
        {/* UND EIN ERLEDIGTES GEWERK LAESST SICH WIEDER OEFFNEN.
            ================================================================
            ORIGINALTEXT MARVIN (09.09.2026)
              „ich muss alte abgeschlossene aufträge wieder öffnen und neu
               terminieren können ... wenn man nochmal hin muss, dann muss das
               möglich sein. das geht derzeit nicht!"

            DER WEG DAHINTER GIBT ES SEIT MIGRATION 001. `POST
            /termine/:id/erledigt` mit `{ erledigt: false }` setzt den Haken
            zurueck und schreibt `wieder_offen` in den Verlauf. Gerufen hat es
            KEINE einzige Zeile: `api.terminErledigt` steht in der Oberflaeche
            genau einmal, mit leerem Rumpf - und ein leerer Rumpf heisst
            „erledigt". **Der Beleg steht in den Daten: 0 Zeilen `wieder_offen`
            in `termin_verlauf`, obwohl der Wert seit Migration im CHECK steht.**

            > Ein Weg, den der Server kann und keine Oberflaeche geht, ist
            > nicht gebaut - und er sieht im Programmcode vollstaendig aus,
            > weil die teure Haelfte dasteht.

            DIE ALTEN DATEN BLEIBEN. Der Termin steht weiter da, das
            Erledigt-Datum wandert in den Verlauf, und wer danach umbucht,
            findet den alten Termin dort wieder. */}
        {darfVergeben && x.erledigt && (
          <button className="knopf haupt" onClick={() => setWas('oeffnen')}>
            Wieder öffnen
          </button>
        )}
        {darf(ich, 'akte.lesen') && (
          <button className={'knopf' + (darfVergeben ? '' : ' haupt')}
                  onClick={zurAkte}>
            Zur Auftragsakte
          </button>
        )}
      </div>
    </Dialog>
  );
}


/**
 * Was an diesem Auftrag zuletzt geschah, und ein Feld, um etwas dazuzusagen.
 *
 * DIE LETZTEN ACHT, NICHT ALLE. Ein Fenster über dem Wochenplan ist kein
 * Archiv; wer den ganzen Verlauf will, geht in die Akte. Acht Zeilen sind
 * das, was man im Stehen liest, bevor man zum Hörer greift.
 *
 * DER KOMMENTAR GEHT AN DEN AUFTRAG, NICHT AN DEN TERMIN. Ein Termin fällt
 * aus und wird neu gebucht; was jemand über die Baustelle gesagt hat, bleibt.
 * Trägt die Karte genau EIN Gewerk, geht der Satz an dieses - dann steht in
 * der Akte, worum es ging.
 */
function Verlauf({ auftragId, ich, gewerk }:
                 { auftragId: string; ich: Anmelder; gewerk?: string }) {
  const [zeilen, setZeilen] = useState<any[] | null>(null);
  const [text, setText] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  async function laden() {
    try { setZeilen(await api.auftragVerlauf(auftragId)); }
    catch { setZeilen([]); }
  }
  useEffect(() => { laden(); }, [auftragId]);
  useFrisch(laden);

  /* DIESELBEN ZWEI RECHTE WIE DER WEG DAHINTER (`verlangtEines`): der
     Innendienst schreibt über `auftrag.schreiben`, der Monteur über
     `akte.rueckmeldung`. Ein Feld, das die Oberfläche zeigt und der Server
     abweist, ist schlimmer als keines. */
  const darfSchreiben = darf(ich, 'auftrag.schreiben')
    || darf(ich, 'akte.rueckmeldung');

  return (
    <div className="terminverlauf">
      <div className="reihe" style={{ justifyContent: 'space-between',
                                      alignItems: 'baseline' }}>
        <b>Auftragsinfo</b>
        {zeilen && zeilen.length > 8 && (
          <span className="klein leise">
            die letzten 8 von {zeilen.length}
          </span>
        )}
      </div>

      <Fehlerkasten lage={fehler} />

      {!zeilen && <p className="klein leise">Wird geladen.</p>}
      {zeilen?.length === 0 && (
        <p className="klein leise">
          Hier steht noch nichts. Was Sie unten schreiben, ist der erste
          Eintrag.
        </p>
      )}
      {/* DIESELBE LISTE WIE IN DER AKTE, und zwar dieselbe.
          Hier stand eine zweite Fassung: der volle Name statt des kurzen,
          der ROHE Quellwert daneben („System, system" - das war Marvins
          „zweimal System"), und kein Gesicht. Die ABFRAGE dahinter ist seit
          dieser Version ebenfalls dieselbe, und damit fallen auch die
          GELÖSCHTEN Einträge heraus, die hier noch standen.

          OHNE HANDGRIFFE: berichtigen und löschen gehören in die Akte. Ein
          Fenster über dem Wochenplan ist zum Lesen und für den einen Satz,
          den man gerade sagen will. */}
      {zeilen && zeilen.length > 0 && (
        <Auftragsinfoliste zeilen={zeilen} deckel={8} />
      )}

      {darfSchreiben && (
        <>
          {/* KEIN „SPEICHERN" NEBEN EINEM LEEREN FELD. Der Knopf steht erst
              da, wenn etwas drinsteht - sonst ist er die halbe Zeit ein
              Knopf, der nichts tut. */}
          <textarea
            className="feld" rows={2} value={text}
            placeholder={'Was ist hier gerade los? Der Satz steht danach in '
                       + 'der Akte, mit Ihrem Namen.'}
            onChange={(e) => setText(e.target.value)}
            disabled={laeuft} />
          {text.trim() && (
            <div className="reihe" style={{ marginTop: 6 }}>
              <button className="knopf klein haupt" disabled={laeuft}
                      onClick={async () => {
                        setLaeuft(true); setFehler(null);
                        try {
                          await api.auftragKommentar(auftragId, text.trim(),
                                                     gewerk);
                          setText(''); await laden();
                        } catch (f) { setFehler(ausFehler(f)); }
                        finally { setLaeuft(false); }
                      }}>
                {laeuft ? 'Einen Moment' : 'In die Akte schreiben'}
              </button>
              <button className="knopf klein" disabled={laeuft}
                      onClick={() => setText('')}>Verwerfen</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}


/**
 * Einen bestehenden Termin verschieben.
 *
 * ES IST DERSELBE WEG WIE DAS ERSTMALIGE BUCHEN (`POST /auftraege/:id/termin`):
 * er schreibt auf das vorhandene Gewerk und läuft dabei durch dieselben
 * Riegel - Gemeindevorlauf, Anordnung, Doppelvergabe. Ein zweiter Weg mit
 * weniger Prüfungen wäre ein Loch: man müsste nur „umbuchen" statt „buchen"
 * sagen, um an ihnen vorbeizukommen.
 *
 * DIE KARTE KANN ZWEI GEWERKE TRAGEN. Einblasen und Spleissen sind dieselbe
 * Fahrt; sie werden zusammen verschoben (`auch_partner`), sonst steht die
 * eine Hälfte nächste Woche und die andere heute.
 */
function Umbuchen({ x, anschrift, zu, fertig }: any) {
  /* DIE GEWÄHLTEN TAGE MIT IHREN ZEITEN, vorbelegt mit dem, was gebucht IST.
     ------------------------------------------------------------------------
     WER EINEN MEHRTÄGIGEN TIEFBAU VERSCHIEBT, VERSCHIEBT ALLE SEINE TAGE.
     Ohne `tage` schickt der Weg genau EINEN Tag, der Server räumt die
     Folgetage in `gewerk_tag` ab - und aus einem dreitägigen Tiefbau wurde
     beim Umbuchen ein eintägiger, ohne Meldung. */
  /* DER ALTE TAG IST KEINE AUSWAHL (.440).
     ======================================================================
     ORIGINALTEXT MARVIN (21.10.2026, Schleusenweg 5 und 10)
       „beim Umbuchen kam es zu diesen Problemen. Nicht direkt eine
        Fehlermeldung, er hat aber halt die Änderungen nicht gemacht. ...
        Das Ähnliches hatten wir jetzt auch gerade eben beim Tiefbau."

     Hier stand der bisherige Termin als erster gewählter Tag vor. Beim
     Tiefbau darf der Kalender mehrere Tage, und ein Klick auf den neuen Tag
     LEGTE IHN DAZU statt ihn zu ersetzen: `tage` war [alt, neu], gebucht
     wurde tage[0], also der alte Tag, der neue hing als Folgetag dran.
     Schleusenweg 10 am 21.10.2026, dreimal hintereinander: „TB storniert
     (08.10.2026): Neuer Termin" und gleich danach „TB gebucht auf
     08.10.2026" - derselbe Tag, der gerade storniert worden war. Bei
     Einblasen und Spleißen ersetzte der Klick den Tag, dort zeigte das
     Fenster nur den alten Termin mit seinen Zeiten, solange niemand klickte.

     Was bisher steht, sagt die Zeile „Bisher:" darunter. Die Auswahl fängt
     leer an, der Kalender steht auf dem Monat des alten Termins. */
  const [tage, setTage] = useState<Bautag[]>([]);
  const [monat, setMonat] = useState(
    /* IN DEUTSCHER ZEIT. Hier stand `new Date().toISOString()` - in derselben
       Datei, die 390 Zeilen weiter oben `toISOString()` ausdruecklich
       verbietet. */
    () => String(x.termin_datum ?? heuteIso()).slice(0, 7));
  const [sub, setSub] = useState(x.firma_id ?? '');
  const [subs, setSubs] = useState<any[]>([]);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  /* Nach dem Umbuchen dieselben zwei Rückfragen wie in der Akte - es ist
     derselbe Vorgang, nur ein anderer Bildschirm davor. */
  const [nachfrage, setNachfrage] = useState<any>(null);
  const [pruefung, setPruefung] = useState<any>(null);

  useEffect(() => { api.subunternehmer().then(setSubs).catch(() => {}); }, []);

  const gewerk = x.arten[0];

  /* DER KALENDER BRAUCHT DIE FEIERTAGE UND DIE REGELZEITEN, und beide kommen
     vom Server: welcher Tag keiner ist, hängt am Bundesland der Auftrags-PLZ,
     und die Regelzeit hängt am Gewerk. */
  useEffect(() => {
    const q = new URLSearchParams({ gewerk, monat });
    if (tage.length) q.set('datum', tage[0].iso);
    api.terminpruefung(x.auftrag_id, q).then(setPruefung).catch(() => setPruefung(null));
  }, [gewerk, monat, tage.length ? tage[0].iso : '', x.auftrag_id]);
  const montage = x.arten.length > 1
    || gewerk === 'EB' || gewerk === 'SP';
  const passende = subs.filter((s: any) =>
    s.aktiv && (!s.gewerke?.length || s.gewerke.includes(gewerk)));

  if (nachfrage) {
    return (
      <Nachfragen auftragId={x.auftrag_id} nachfrage={nachfrage}
                  baustelle={anschrift}
                  fertig={() => { setNachfrage(null); fertig(); }} />
    );
  }

  return (
    <Dialog titel={`Termin umbuchen`} untertitel={anschrift}
            laeuft={laeuft} zu={zu} breite={620}>
      <Fehlerkasten lage={fehler} />
      <p className="klein leise" style={{ marginTop: 0 }}>
        Bisher: {datum(x.termin_datum)}
        {x.termin_von ? `, ${uhr(x.termin_von)}` : ''}
        {x.termin_bis ? ` bis ${uhr(x.termin_bis)}` : ''}
        {' '}&middot;{' '}
        {x.arten.map((a: string) => GEWERK_TEXT[a]).join(' und ')}
      </p>
      <Auswahl name="subunternehmer_id" titel="Firma" wert={sub} setze={setSub}
               breite={340} fehler={fehler} leerText="noch offen"
               hinweis="Der Kalender zeigt, wo diese Firma schon fährt."
               optionen={passende.map((s: any) => ({ wert: s.id, text: s.firma }))} />

      {/* DERSELBE KALENDER WIE IN DER AKTE.
          Umbuchen ist derselbe Vorgang wie Buchen, nur mit einem Termin
          davor - zwei verschiedene Bildschirme für dieselbe Frage wären zwei
          Bedienungen, von denen man die eine kennt und die andere nicht. */}
      <Terminkalender p={pruefung} monat={monat} setMonat={setMonat} firma={sub}
                      tage={tage} setTage={setTage}
                      ausser={x.ids}
                      mehrtag={!!pruefung?.mehrtagErlaubt} />

      {montage && x.arten.length > 1 && (
        <p className="klein leise">
          Einblasen und Spleißen werden zusammen verschoben. Es ist dieselbe
          Fahrt.
        </p>
      )}
      <div className="reihe" style={{ marginTop: 12 }}>
        <button className="knopf haupt" disabled={laeuft || !tage.length}
                onClick={async () => {
                  setLaeuft(true); setFehler(null);
                  try {
                    const r = await api.terminBuchen(x.auftrag_id, {
                      gewerk,
                      datum: tage[0].iso, von: tage[0].von, bis: tage[0].bis,
                      subunternehmer_id: sub || null,
                      auch_partner: x.arten.length > 1,
                      /* GEBUCHT WIRD, WAS IM KALENDER STEHT - hier wie in der
                         Akte, und mit denselben Chips davor. */
                      tage: tage.length > 1 ? tage : undefined,
                    });
                    if (r?.nachfrage) { setNachfrage(r.nachfrage); return; }
                    fertig();
                  } catch (f) { setFehler(ausFehler(f)); }
                  finally { setLaeuft(false); }
                }}>
          {laeuft ? 'Einen Moment' : 'Umbuchen'}
        </button>
        <button className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
      </div>
    </Dialog>
  );
}


/**
 * Dieselben Termine, nach Tag untereinander.
 *
 * LEERE TAGE STEHEN NICHT DRIN, und das ist der Unterschied zum Wochenraster:
 * dort ist ein leerer Tag eine Aussage („hier ist Platz"), hier wäre er eine
 * leere Zeile in einer Fahrtenliste. Wer die Liste liest, will wissen, wo er
 * hinfährt - nicht, wo er nicht hinfährt.
 *
 * JEDE ZEILE IST ANKLICKBAR und öffnet dasselbe Fenster wie eine Kachel im
 * Kalender: Anschrift, Kunde, Weg zur Baustelle, und für den Innendienst das
 * Umbuchen. Ein zweiter Weg mit eigenen Riegeln wäre ein zweiter Ort zum
 * Pflegen.
 */
function Terminliste(
  { tage, jeTag, gemeldet, istSub, waehle }:
  { tage: Tag[]; jeTag: (t: Tag) => any[]; gemeldet: (t: Tag) => any[];
    istSub: boolean; waehle: (x: any) => void }
) {
  const heute = heuteTag();
  const mitTerminen = tage
    .map((t) => ({ t, liste: jeTag(t), meldung: gemeldet(t) }))
    .filter((x) => x.liste.length > 0);

  if (mitTerminen.length === 0) {
    return (
      <div className="karte">
        <div className="leib">
          <p className="leise" style={{ margin: 0 }}>
            In diesem Zeitraum steht kein Termin. Mit den Pfeilen oben kommen
            Sie in die nächste Woche.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {mitTerminen.map(({ t, liste, meldung }) => (
        <div className="karte" key={t}>
          <h3>
            {/* DER WOCHENTAG KAM AUS `toLocaleDateString` - also aus der Uhr
                UND der Sprache des Betrachters. Er kommt jetzt aus dem Tag
                selbst, gerechnet und auf Deutsch. */}
            {wochentag(t)},{' '}
            {langTag(t)}
            {t === heute && <span className="merker">heute</span>}
            <span className="nebenbei">
              {liste.length === 1 ? '1 Termin' : `${liste.length} Termine`}
            </span>
            {/* WAS AN DIESEM TAG GEMELDET IST, GEHÖRT DANEBEN. Ein Urlaubstag
                mit einem Termin darauf ist genau die Zeile, bei der jemand
                anrufen muss. */}
            {meldung.filter((v: any) => v.zustand !== 'verfuegbar')
                    .map((v: any, i: number) => (
              <span key={i} className="merker warnung">
                {v.zustand === 'urlaub' ? 'Urlaub' : 'nicht verfügbar'}
                {!istSub && v.firma ? ', ' + v.firma : ''}
              </span>
            ))}
          </h3>
          <div className="rollt">
            <table className="liste">
              <thead>
                {/* DIE ANORDNUNG, SO WIE MAN SIE LIEST.
                    ORIGINALTEXT MARVIN (02.09.2026): „hier ist die
                    darstellung nicht richtig, passe überall die anordnung an
                    ... außerdem fehlt die SM und KLS nummer in der übersicht,
                    als auch die kontaktnummern(zum direkten wählen auch
                    wieder)"
                    Zeit, Gewerk, Baustelle - und dann die zwei Zahlen, die am
                    Telefon genannt und in Portale getippt werden, in einer
                    EIGENEN Spalte. Unter der Baustelle standen sie zwischen
                    Postleitzahl und Kundennamen und waren nicht zu finden. */}
                <tr>
                  <th style={{ width: 92 }}>Zeit</th>
                  <th style={{ width: 120 }}>Gewerk</th>
                  <th>Baustelle</th>
                  <th style={{ width: 130 }}>KLS / SM</th>
                  <th style={{ width: 150 }}>Anruf</th>
                  {!istSub && <th>Firma</th>}
                  <th style={{ width: 110 }}>Stand</th>
                </tr>
              </thead>
              <tbody>
                {liste.map((x: any) => (
                  <tr key={x.id + (x.termin_datum ?? '')}
                      onClick={() => waehle(x)}
                      title="Öffnet den Termin mit dem Weg zur Baustelle.">
                    <td className="nichtumbrechen">
                      {x.termin_von ? uhr(x.termin_von) : '-'}
                      {x.termin_bis && <> bis {uhr(x.termin_bis)}</>}
                    </td>
                    <td>
                      {(x.arten ?? [x.art]).map((a: string) =>
                        GEWERK_TEXT[a] ?? a).join(' + ')}
                      {/* WELCHER TAG VON WIE VIELEN.
                          Ein mehrtägiger Tiefbau steht an jedem seiner Tage
                          im Plan (sonst sähe der zweite Tag frei aus). Ohne
                          diese Zeile sähen drei Tage aus wie drei Termine an
                          derselben Baustelle - und jemand würde zwei davon
                          absagen. */}
                      {x.bautage > 1 && (
                        <div className="klein leise">
                          Tag {x.tag_nr} von {x.bautage}
                        </div>
                      )}
                    </td>
                    <td>
                      <b>{x.strasse} {x.hausnummer}{x.hausnummer_zusatz ?? ''}</b>
                      <div className="klein leise">
                        {x.plz} {x.ort_text}
                        {x.kunde_name ? ', ' + x.kunde_name : ''}
                      </div>
                    </td>
                    <td className="kennung klein">
                      {x.kls_id ?? <span className="leise">KLS fehlt</span>}
                      <div className="leise">
                        {x.sm_nummer ?? 'SM fehlt'}
                      </div>
                    </td>
                    {/* ZUM DIREKTEN WÄHLEN. Der Monteur steht vor dem Haus und
                        niemand macht auf; ein Klick auf die Zeile daneben
                        öffnet den Termin, ein Klick auf den Knopf wählt. Der
                        Klick darf deshalb NICHT nach oben durchlaufen -
                        sonst geht beim Wählen das Fenster mit auf. */}
                    <td className="knopfleiste"
                        onClick={(e) => e.stopPropagation()}>
                      {x.kunde_telefon && (
                        <a className="knopf" href={'tel:' + x.kunde_telefon}
                           title={x.kunde_telefon}>Festnetz</a>
                      )}
                      {x.kunde_mobil && (
                        <a className="knopf" href={'tel:' + x.kunde_mobil}
                           title={x.kunde_mobil}>Mobil</a>
                      )}
                      {!x.kunde_telefon && !x.kunde_mobil
                        && <span className="leise klein">keine Nummer</span>}
                    </td>
                    {!istSub && <td>{x.firma ?? <span className="leise">offen</span>}</td>}
                    <td>
                      {x.erledigt
                        ? <span className="gewerk fertig">erledigt</span>
                        : x.restarbeiten_offen
                          ? <span className="merker warnung">Restarbeiten</span>
                          : <span className="leise">offen</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </>
  );
}


export default function Terminplaner({
  ich, oeffne, ansicht = 'liste',
}: { ich: Anmelder;
     oeffne: (id: string, reiter?: string) => void;
     /** Welche Ansicht dieser MENÜPUNKT zeigt. Siehe Migration 085. */
     ansicht?: 'plan' | 'liste' }) {
  // ZWEI ANSICHTEN AUF DIESELBE FRAGE. Der Wochenplan sagt, wer WANN WAS tut;
  // die Verfügbarkeit sagt, wer wann überhaupt kann. Früher waren das zwei
  // Menüpunkte („Wochenplan" und „Verfügbarkeit pflegen") und ein dritter für
  // die Frage, von wem die Meldung fehlt.
  /* DREI ANSICHTEN, UND DIE LISTE IST DIE NEUE.
   *
   * ORIGINALTEXT MARVIN (31.08.2026, zur Auftragsliste einer Partnerfirma)
   *   „es fehlt die übersicht wie die planung ist, wo er hinfahren muss. das
   *    kann er so garnicht auslesen ... sowohl als liste als auch den
   *    kalender, den wir im innendienst haben und der sub dann auf den
   *    eintrag klicken kann."
   *
   * DER KALENDER ZEIGT DIE WOCHE, DIE LISTE DEN NÄCHSTEN TAG. Beides sind
   * dieselben Termine; was sich unterscheidet, ist die Frage. Wer morgens ins
   * Auto steigt, will keine zwei Wochen sehen, sondern die nächsten fünf
   * Adressen untereinander - mit Uhrzeit, Gewerk und einem Klick in die Akte.
   *
   * Für den Partner ist die LISTE die Voreinstellung, für den Innendienst der
   * PLAN: der eine fährt hin, der andere disponiert. */
  /* UND DIE VERFÜGBARKEIT IST HIER WEG (31.08.2026).
   *
   * ORIGINALTEXT MARVIN
   *   „mach die terminliste und wochenplanung als eigenen vollen menüpunkt,
   *    die verfügbarkeit kannst du da entfernen, die war bereits schon drin."
   *
   * ER HAT RECHT, UND ES WAR DIESELBE FALLE WIE BEI DEN VORDRUCKEN: die
   * Verfügbarkeit steht seit Migration 044 als eigener Menüpunkt da UND als
   * Reiter hier. **Zwei Wege zu derselben Sache sind kein doppelter Komfort,
   * sondern eine halbe Auskunft** - wer den einen kennt, erfährt vom anderen
   * nie, und wer sie über den Reiter pflegt, sucht sie später im Menü. Der
   * Menüpunkt bleibt, der Reiter fällt.
   *
   * Was hier bleibt, sind die drei Ansichten auf DIESELBEN Termine: der Plan
   * für den, der disponiert, die Liste für den, der hinfährt, und die
   * Glasfaserliste für die Telekom. */
  /* DER MENÜPUNKT ENTSCHEIDET, WELCHE ANSICHT DASTEHT - NICHT DIE
     FENSTERBREITE.

     ORIGINALTEXT MARVIN (01.09.2026)
       „der wochenplan soll seitlich in das menü wandern und oben raus beim
        terminplaner."

     Hier stand bis heute eine Rechnung: unter 700 Pixeln die Liste, darüber
     das Raster, und für einen Partner immer die Liste. Das war die beste
     Antwort, solange es eine Antwort geben musste - jetzt gibt es zwei
     Menüpunkte, und **eine Voreinstellung, die von der Fenstergrösse abhängt,
     ist eine Entscheidung, die jemand für den Benutzer trifft, ohne ihn zu
     kennen.**

     `wo` bleibt trotzdem ein Zustand: die Glasfaserliste ist eine dritte
     Ansicht auf dieselben Termine und hängt weiter als Reiter am
     Terminplaner. Ein eigener Menüpunkt dafür wäre einer zu viel. */
  const [wo, setWo] = useState<'plan' | 'liste' | 'glasfaser'>(ansicht);
  // Wer den Menüpunkt wechselt, wechselt die Ansicht. Ohne diese Zeile bliebe
  // die Komponente auf der Ansicht stehen, mit der sie einmal geöffnet wurde.
  useEffect(() => { setWo(ansicht); }, [ansicht]);
  /* DIE ANSICHT BLEIBT, WIE MAN SIE VERLASSEN HAT (.331).
   *
   * ORIGINALTEXT MARVIN (07.10.2026)
   *   „wenn ich auf dem wochenplan, habe dort einstellungen von woche
   *    (gescrollt) oder/und Firma gemacht, gehe dann von dort in einen auftrag
   *    und dann wieder zurück in den wochenplan dann soll er meine vorherigen
   *    einstellungen gespeichert haben. ich muss derzeit immer wieder alles
   *    von neu eingeben und eintragen. das ist störend."
   *
   * Gemerkt werden Wochenbeginn, Firma, Suchwort und die Stelle, bis zu der
   * gerollt war: im Gerät (localStorage), je Benutzer und je Ansicht, wie die
   * Ansicht der Arbeitsliste. Ohne Speicher (privates Fenster) gilt heute. */
  const MERK_SCHLUESSEL = `mds.ansicht.terminplan.${ansicht}.${ich.benutzerId}`;
  /* DIE AKTUELLE WOCHE ZUERST (.358). ORIGINALTEXT MARVIN (14.10.2026)
       „wenn du in die Wochenplanung gehst, dass er manchmal nicht die
        aktuelle Kalenderwoche anzeigt, sondern die davor ... dass dann auch
        quasi heute die aktuelle Woche angezeigt wird."
     Die gemerkte Woche stammte vom letzten Besuch, auch von gestern oder
     letzter Woche. Jetzt gilt sie nur für den Weg zurück aus einem Auftrag
     (höchstens 2 Stunden alt). Wer später wiederkommt, steht in der
     laufenden Woche. Firma und Suchwort bleiben gemerkt. */
  const gemerkt = (() => {
    try {
      const roh = window.localStorage.getItem(MERK_SCHLUESSEL);
      const g = roh ? JSON.parse(roh) : null;
      if (g && !(Number(g.am ?? 0) > Date.now() - 2 * 3600 * 1000)) { delete g.start; delete g.rollstelle; }
      return g;
    } catch { return null; }
  })();
  const [start, setStart] = useState<Tag>(() => {
    const jetzt = montag(heuteTag());
    const g = gemerkt?.start && /^\d{4}-\d{2}-\d{2}$/.test(gemerkt.start) ? gemerkt.start : null;
    return g ?? jetzt;
  });
  /* ZWÖLF WOCHEN, UND ZWAR IMMER.
   *
   * ORIGINALTEXT MARVIN (18.09.2026)
   *   „bei dem wochenplan und terminplan kannst du die auswahl der wochen
   *    entfernen. wir zeigen standardmäßig 12 wochen an im vorlauf."
   *
   * Hier stand ein Auswahlfeld mit 1/2/4/6/8/10 und der Vorgabe 2. Es hat
   * jeden Blick auf den Plan mit einer Frage angefangen, die immer dieselbe
   * Antwort hatte: **wer disponiert, will den Vorlauf sehen, den er zusagen
   * muss.** Die Telekom bekommt eine Kapazitätsmeldung über 12 Wochen
   * (`kapazitaet.vorlauf_wochen`, Standard 12) - der Plan zeigt jetzt
   * dieselbe Spanne, und damit stehen Zusage und Plan nebeneinander auf
   * derselben Strecke.
   *
   * Es ist eine Konstante und kein Zustand: ein Wert, den niemand mehr
   * ändern kann, gehört nicht in `useState`. */
  const WOCHEN = 12;
  const wochen = WOCHEN;
  /* EIN PLAN JE FIRMA.
   *
   * ORIGINALTEXT MARVIN (26.08.2026)
   *   „beim wochenplan will ich nicht einen gemeinsamen sondern für jede firma
   *    einen zur besseren übersicht. baue oben ein dropdown ein."
   *
   * FÜNF FIRMEN IN EINEM RASTER SIND FÜNF KALENDER ÜBEREINANDER. Wer wissen
   * will, ob Brester Bau am Donnerstag noch kann, liest sich durch die
   * Termine von vier anderen. Gefiltert wird auf dem SERVER: dann fallen
   * Urlaub und Sperren der anderen gleich mit weg - eine Firma, deren Termine
   * man nicht sieht, deren Urlaub aber schon, ist die verwirrendere Hälfte.
   *
   * „Alle Firmen" bleibt die Voreinstellung: der Innendienst disponiert
   * zuerst über alle und geht danach in die einzelne. */
  const [firma, setFirma] = useState<string>(() => String(gemerkt?.firma ?? ''));
  /* DIESELBE SUCHE WIE IN DER ARBEITSLISTE.
   *
   * ORIGINALTEXT MARVIN (08.09.2026)
   *   „beim wochenplan brauchen wir die gleiche suche nach aufträgen wie den
   *    aufträgens elbst. er soll wenn gesucht werden dann auf allen firmen
   *    suchen, und den gesamtkalender nur mit dem gesuchten auftrag anszeigen,
   *    wo er terminiert ist, alle anderen einträge werden ausgeblendet"
   *
   * Wonach gesucht wird, steht in `regeln/auftragsuche.ts` und wird vom
   * Server gerechnet - die Oberfläche schickt das Wort und baut keine zweite
   * Fassung daneben. */
  const [suche, setSuche] = useState<string>(() => String(gemerkt?.suche ?? ''));
  const [d, setD] = useState<any>(null);
  /* … und gemerkt wird bei jeder Änderung, die Rollstelle beim Verlassen. */
  const [rollstelleGesetzt, setRollstelleGesetzt] = useState(false);
  useEffect(() => {
    try {
      const alt = JSON.parse(window.localStorage.getItem(MERK_SCHLUESSEL) ?? '{}');
      window.localStorage.setItem(MERK_SCHLUESSEL, JSON.stringify({ ...alt, start, firma, suche, am: Date.now() }));
    } catch { /* ohne Speicher geht der Plan trotzdem */ }
  }, [start, firma, suche, MERK_SCHLUESSEL]);
  useEffect(() => {
    const merkeRollstelle = () => {
      try {
        const buehne = document.querySelector<HTMLElement>('.buehne');
        const alt = JSON.parse(window.localStorage.getItem(MERK_SCHLUESSEL) ?? '{}');
        window.localStorage.setItem(MERK_SCHLUESSEL, JSON.stringify({ ...alt, rollstelle: buehne ? buehne.scrollTop : window.scrollY, am: Date.now() }));
      } catch { /* siehe oben */ }
    };
    /* BEIM ROLLEN GEMERKT, nicht erst beim Verlassen: beim Abbau der Seite
       ist die Bühne schon weg. Gebremst, damit nicht jeder Bildpunkt schreibt. */
    let t: ReturnType<typeof setTimeout> | null = null;
    const gebremst = () => { if (t) clearTimeout(t); t = setTimeout(merkeRollstelle, 250); };
    const buehne = document.querySelector<HTMLElement>('.buehne');
    (buehne ?? window).addEventListener('scroll', gebremst, { passive: true } as any);
    window.addEventListener('beforeunload', merkeRollstelle);
    return () => {
      if (t) clearTimeout(t);
      (buehne ?? window).removeEventListener('scroll', gebremst);
      window.removeEventListener('beforeunload', merkeRollstelle);
    };
  }, [MERK_SCHLUESSEL, d]);
  /* WO DER BLICK NACH DEM BLÄTTERN LANDET (.269).
   *
   * ORIGINALTEXT MARVIN (01.10.2026)
   *   „wenn ich hier vor oder zurück klicke, dann wechselt ehr zwar den KW
   *    bereich, bleibt bei seinem scollen aber genau da stehen wo er war ...
   *    er soll da anknpüfen mit der KW wo ich aufgehört habe."
   *
   * Zurück heißt: die Woche direkt vor der bisher ersten, also die LETZTE der
   * neuen Spanne. Vor heißt: die Woche direkt nach der bisher letzten, also
   * die ERSTE. Gescrollt wird erst, wenn die neue Spanne geladen ist. */
  const [anker, setAnker] = useState<'anfang' | 'ende' | null>(null);
  const [dFuer, setDFuer] = useState('');
  /* WOHIN SCHON GESPRUNGEN WURDE. Ohne diesen Merker zöge der Sprung die
     Woche bei JEDEM Nachladen wieder zurück - und wer danach weiterblättert,
     käme nicht von der Stelle. Gesprungen wird EINMAL je Suchwort. */
  const [gesprungen, setGesprungen] = useState('');
  // Der angeklickte Termin. Er wird ANGESEHEN, nicht angesprungen.
  const [offen, setOffen] = useState<any>(null);

  const ende = plusTage(start, wochen * 7 - 1);

  /* NACHLADEN MUSS VON AUSSEN GEHEN. Wer im Terminfenster umbucht oder
     storniert, ändert genau das, was der Plan hinter dem Fenster zeigt; ohne
     diesen Weg stünde der alte Termin noch da, und beim nächsten Blick wäre
     er auf einmal woanders. */
  function laden() {
    const p = new URLSearchParams({ von: start, bis: ende });
    if (firma) p.set('firma', firma);
    if (suche.trim()) p.set('suche', suche.trim());
    const fuer = start;
    api.termine(p).then((x) => { setD(x); setDFuer(fuer); }).catch(() => {});
  }
  useEffect(() => {
    if (rollstelleGesetzt || !d || dFuer !== start) return;
    setRollstelleGesetzt(true);
    const ziel = Number(gemerkt?.rollstelle ?? 0);
    if (!(ziel > 0)) return;
    const r = requestAnimationFrame(() => {
      const buehne = document.querySelector<HTMLElement>('.buehne');
      if (buehne) buehne.scrollTop = ziel; else window.scrollTo({ top: ziel });
    });
    return () => cancelAnimationFrame(r);
  }, [d, dFuer, start, rollstelleGesetzt]);
  useEffect(() => {
    if (!anker || dFuer !== start) return;
    const r = requestAnimationFrame(() => {
      const bl = Array.from(document.querySelectorAll<HTMLElement>('.wochenblock'));
      const ziel = anker === 'ende' ? bl[bl.length - 1] : bl[0];
      if (ziel) {
        /* ES ROLLT DIE BÜHNE, NICHT DAS FENSTER. Abgezogen wird der Kopf, der
           oben klebt, damit die Woche nicht unter ihm beginnt. */
        const buehne = ziel.closest<HTMLElement>('.buehne');
        const kopf = (buehne?.querySelector<HTMLElement>('.kopf')?.offsetHeight ?? 80) + 12;
        if (buehne) buehne.scrollTop += ziel.getBoundingClientRect().top - buehne.getBoundingClientRect().top - kopf;
        else window.scrollTo({ top: Math.max(0, ziel.getBoundingClientRect().top + window.scrollY - kopf) });
      }
      setAnker(null);
    });
    return () => cancelAnimationFrame(r);
  }, [d, dFuer, anker, start]);
  /* GEBREMST, WIE IN DER ARBEITSLISTE. Ohne die Bremse holt jeder Buchstabe
     einen ganzen Wochenplan - und Marvin hat genau das am 07.09.2026 an der
     Arbeitsliste beanstandet („er ist die ganze zeit unten am laden ist, es
     flackert, da er im sekundentakt alles neu läd wegen der suche"). */
  useEffect(() => {
    const t = setTimeout(laden, suche.trim() ? 220 : 0);
    return () => clearTimeout(t);
  }, [start, wochen, firma, suche]);
  /* UND NACH JEDEM SCHREIBVORGANG. Der Satz darüber nennt genau den Fall:
     wer umbucht oder storniert, ändert das, was der Plan zeigt. Bis .151
     musste jede Aufrufstelle daran denken, ihn zu rufen. */
  useFrisch(laden);

  /* DER PLAN SPRINGT DORTHIN, WO DER GESUCHTE AUFTRAG TERMINIERT IST.
   *
   * „wo er terminiert ist" - und das ist fast nie die Woche, die gerade offen
   * steht. **Eine leere Woche beantwortet die Frage nicht, sondern stellt sie
   * noch einmal**, und sie sieht dabei aus wie „den Auftrag gibt es nicht".
   *
   * GESPRUNGEN WIRD NUR, WENN IN DER OFFENEN SPANNE KEIN EINZIGER TREFFERTAG
   * LIEGT. Sonst nähme der Sprung dem Innendienst die Woche weg, die er
   * gerade ansieht - und der Treffer steht ohnehin schon darin. Und nur
   * EINMAL je Suchwort: danach blättert der Mensch. */
  useEffect(() => {
    const tage: string[] = d?.suche?.tage ?? [];
    if (!suche.trim() || !tage.length) return;
    if (gesprungen === suche.trim()) return;
    const a = start, b = ende;
    if (tage.some((t) => t >= a && t <= b)) { setGesprungen(suche.trim()); return; }
    /* DER ERSTE TAG AB HEUTE, sonst der letzte. Ein Auftrag, dessen Termine
       alle in der Vergangenheit liegen, wird gesucht, WEIL man nachsehen
       will, wann er war. */
    const heute = heuteTag();
    const ziel = tage.find((t) => t >= heute) ?? tage[tage.length - 1];
    setGesprungen(suche.trim());
    setStart(montag(ziel));
  }, [d, suche]);

  const tage: Tag[] = tageAb(start, wochen * 7);

  const jeTag = (t: Tag) => zusammenfassen(
    (d?.termine ?? []).filter((x: any) => x.termin_datum?.slice(0, 10) === t));
  /* ALLE MELDUNGEN DES TAGES, nicht nur der Urlaub.
     Bis zum 26.08.2026 stand hier `zustand === 'urlaub'`, und alles andere
     fiel durch: der Tageskopf sagte „verfügbar", auch wenn gar nichts
     gemeldet war. „Nicht gemeldet" ist eine dritte Auskunft. */
  const gemeldet = (t: Tag) =>
    (d?.verfuegbar ?? []).filter((v: any) => v.datum?.slice(0, 10) === t);
  /* DIE GESPERRTEN ZEITEN GIBT ES NICHT MEHR.
   *
   * ORIGINALTEXT MARVIN (18.09.2026)
   *   „die sperre bei den terminen muss raus. diese wirkt sich auch auf den
   *    wochenplan aus. (steht ganz unten) wenn termine gelöscht werden muss
   *    der zeitslot wieder frei werden, wir brauchen keine sperre dort für
   *    betriebsschulungen, urlaub oder sonstiges. entweder er ist gebucht
   *    oder nicht."
   *
   * Hier stand `gesperrt(t)` aus `d.blockungen`, und das Raster hat daraus
   * eine Schraffur gezeichnet. **Ein Zeitfenster hat ab jetzt genau zwei
   * Zustände: es steht ein Termin darin oder nicht.** Alles, was daneben
   * einen Slot unbenutzbar machte, ist gefallen - samt Tabelle, Route und
   * Tabelle in der Datenbank (Migration 257).
   *
   * Der ganze Tag lässt sich weiterhin melden: das ist die Verfügbarkeit,
   * und die meldet die Firma selbst. Sie ist der Weg für Urlaub. */

  return (
    <>
      <Kopf
        /* DIE ÜBERSCHRIFT SAGT, WO MAN IST, und das ist seit Migration 085
           der Menüpunkt und nicht mehr der Reiter. Stünde über dem Raster
           „Terminplaner", suchte man den Wochenplan weiter. */
        titel={ansicht === 'plan' && wo !== 'glasfaser'
               ? 'Wochenplan' : 'Terminplaner'}
        weg={wo === 'plan'
          ? `${langTag(start)} bis ${langTag(ende)}`
            + (firma
                ? ', ' + ((d?.firmen ?? []).find((f: any) => f.id === firma)
                            ?.firma ?? 'eine Firma')
                : '')
          : wo === 'liste'
            ? `${langTag(start)} bis ${langTag(ende)}, nach Tag sortiert`
          : 'Die Wochenliste für die Telekom'}
        rechts={(wo !== 'plan' && wo !== 'liste') ? undefined : (
          <div className="reihe">
            <button className="knopf" onClick={() => {
              setAnker('ende'); setStart(plusTage(start, -wochen * 7));
            }}>&larr;</button>
            <button className="knopf" onClick={() => { setAnker('anfang'); setStart(montag(heuteTag())); }}>Heute</button>
            <button className="knopf" onClick={() => {
              setAnker('anfang'); setStart(plusTage(start, wochen * 7));
            }}>&rarr;</button>
            {/* HIER STAND DAS AUSWAHLFELD FÜR DIE WOCHENZAHL. Es ist weg
                (18.09.2026), der Plan zeigt immer 12 Wochen - die Begründung
                steht oben an der Konstante `WOCHEN`.
                DIE PFEILE SPRINGEN UM DIE GEZEIGTE SPANNE, also um 12 Wochen.
                Das bleibt: ein Vor und Zurück, das eine andere Strecke nimmt
                als die Ansicht breit ist, lässt Tage zwischen zwei Blicken
                verschwinden. */}
            {/* NICHT FÜR DEN SUB. Er sieht ohnehin nur seine eigene Firma;
                eine Auswahl mit genau einem Eintrag ist keine Auswahl, und
                eine mit den Namen der anderen darin wäre eine Auskunft, die
                ihn nichts angeht. */}
            {/* UND SIE IST WÄHREND EINER SUCHE ABGESCHALTET.
                Der Server lässt sie dann fallen („er soll wenn gesucht werden
                dann auf allen firmen suchen"), und **ein Auswahlfeld, das
                nichts mehr ändert, ist keine Einstellung, sondern eine
                Falle**: man stellt es und glaubt, man hätte etwas
                eingestellt. Der Titel sagt, warum. */}
            {!ich.istSub && (
              <select value={firma} onChange={(e) => setFirma(e.target.value)}
                      disabled={!!suche.trim()}
                      title={suche.trim()
                        ? 'Während einer Suche wird über alle Firmen gesucht'
                        : 'Den Plan auf eine Firma verkleinern'}>
                <option value="">Alle Firmen</option>
                {(d?.firmen ?? []).map((f: any) => (
                  <option key={f.id} value={f.id}>{f.firma}</option>
                ))}
              </select>
            )}
          </div>
        )}
      />
      {/* DIE ZWEI REITER SIND WEG (Migration 085). „Wochenplan" und
          „Terminliste" sind seit dem 01.09.2026 zwei Menüpunkte, und ein
          zweiter Weg zu derselben Sache ist kein doppelter Komfort, sondern
          eine halbe Auskunft: wer den einen kennt, erfährt vom anderen nie.
          Die Glasfaserliste bleibt hier - sie ist eine Ansicht FÜR DIE
          TELEKOM auf dieselben Termine und hat für sich keinen Menüpunkt
          verdient. */}
      {/* NICHT FÜR DEN SUB. Die Glasfaserliste geht an die Telekom und trägt
          die Termine ALLER Firmen. Der Reiter stand für ihn trotzdem da und
          endete in einer weissen Fläche: der Datenweg dahinter verlangt
          `termin.vergeben`, gab 403, und die Tafel schwieg dazu. Ein Reiter,
          der nichts zeigt und nichts sagt, sieht aus wie ein Fehler im
          System.

          UND EINE LEERE LEISTE IST AUCH KEINE. Seit der Wochenplan ein
          Menüpunkt ist, wäre die Reiterzeile für den Partner ein leerer
          Streifen mit einer Linie darunter - vier Pixel, die etwas
          ankündigen, das nicht kommt. Sie entsteht deshalb gar nicht erst.

          UND IM WOCHENPLAN AUCH NICHT: dort geht es um Kapazität, nicht um
          die Meldung an die Telekom. Zwei Wege zur Glasfaserliste wären
          wieder eine halbe Auskunft. */}
      {!ich.istSub && ansicht !== 'plan' && (
        <div className="reiter">
          <button aria-current={wo === 'liste'} onClick={() => setWo('liste')}>
            Terminliste
          </button>
          <button aria-current={wo === 'glasfaser'}
                  onClick={() => setWo('glasfaser')}>
            Glasfaser-Termine
          </button>
        </div>
      )}
      <div className="inhalt">

        {/* DAS SUCHFELD STEHT ÜBER DEM KALENDER, wie in der Arbeitsliste über
            der Liste - und mit DEMSELBEN Platzhalter: er nennt die Wege, die
            es wirklich gibt, und **ein Platzhalter, der eine Suchmöglichkeit
            verschweigt, ist so gut wie keine.** Für die Glasfaserliste steht
            er nicht da: die geht an die Telekom und ist keine Auftragssuche. */}
        {(wo === 'plan' || wo === 'liste') && (
          <div className="reihe" style={{ marginBottom: 10 }}>
            <input
              type="search" autoComplete="off"
              placeholder={'Auftrag im Plan suchen: Adresse, Ort, KLS, '
                           + 'SM-Nummer, Kunde'
                           + (ich.istSub ? '' : ', BA/FA-ID, Bauauftrag-ID')}
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
              style={{ flex: '1 1 340px', maxWidth: 520 }}
            />
            {suche && (
              <button className="knopf klein"
                      onClick={() => { setSuche(''); setGesprungen(''); }}>
                Suche zurücksetzen
              </button>
            )}
          </div>
        )}

        {/* WAS DIE SUCHE MIT DEM KALENDER MACHT, STEHT DA.
            **Nichts verschwindet still** - im Plan fehlen währenddessen
            Urlaub und Sperren, und ohne diesen Kasten hält jemand den
            Kalender für kaputt. */}
        {d?.sucheAktiv && (wo === 'plan' || wo === 'liste') && (
          <div className={'hinweis' + (d?.suche?.zahl ? '' : ' warnung')}
               style={{ marginBottom: 10 }}>
            <b>
              {d?.suche?.zahl
                ? `Suche „${d.suche.wort}": ${d.suche.zahl} `
                  + `${d.suche.zahl === 1 ? 'Auftrag' : 'Aufträge'}`
                : `Suche „${d?.suche?.wort ?? suche}": kein Auftrag gefunden`}
            </b>
            <div className="klein">
              {d?.suche?.zahl
                ? 'Im Plan stehen nur die Termine dieser Aufträge, über alle '
                  + 'Firmen. Urlaub und gesperrte Zeiten sind währenddessen '
                  + 'ausgeblendet.'
                : 'Gesucht wird über Adresse, Ort, KLS, SM-Nummer und Kunde. '
                  + 'Ein Auftrag, den es nicht gibt, steht in keinem Plan - '
                  + 'nachsehen lässt sich das unter „Aufträge".'}
              {d?.suche?.gekuerzt
                && ' Es werden die ersten 50 Treffer gezeigt; das Suchwort ist '
                 + 'zu weit gefasst.'}
            </div>
            {/* ZWEI LAGEN, ZWEI NÄCHSTE SCHRITTE. „Gibt es nicht" schickt
                jemanden in die Arbeitsliste, „noch nicht terminiert" in die
                Terminvergabe - und ein leerer Kalender sieht in beiden Fällen
                gleich aus. */}
            {d?.suche?.ohneTermin && (
              <div className="klein" style={{ marginTop: 6 }}>
                <b>Dieser Auftrag hat noch keinen Termin.</b> Deshalb steht er
                in keiner Woche. Vergeben wird er in der Auftragsakte unter
                „Termin vergeben".
              </div>
            )}
            {/* UND DIE TAGE, AN DENEN ER WIRKLICH STEHT - als Weg dahin und
                nicht als Auskunft. Der Plan springt beim Suchen von selbst
                zur ersten; wer mehrere Termine hat, kommt hier zu den
                anderen. */}
            {(d?.suche?.tage ?? []).length > 0 && (
              <div className="reihe" style={{ marginTop: 8, flexWrap: 'wrap' }}>
                <span className="klein leise">Terminiert am</span>
                {(d.suche.tage as string[]).map((t) => {
                  const drin = t >= start && t <= ende;
                  return (
                    <button key={t}
                            className={'knopf klein' + (drin ? ' haupt' : '')}
                            onClick={() => setStart(
                              montag(t))}>
                      {datum(t)}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {wo === 'glasfaser' && <Glasfasertermine ich={ich} />}

        {wo === 'plan' && (
          <Wochenraster
            tage={tage} termineAm={jeTag} gemeldetAm={gemeldet}
            firmenAktiv={d?.firmenAktiv ?? 0}
            ohneLage={!!d?.sucheAktiv}
            waehle={setOffen} />
        )}

        {wo === 'liste' && (
          <Terminliste tage={tage} jeTag={jeTag} gemeldet={gemeldet}
                       istSub={ich.istSub} waehle={setOffen} />
        )}

        {/* HIER STAND DIE KARTE „GESPERRTE ZEITEN".
            ---------------------------------------------------------------
            ORIGINALTEXT MARVIN (18.09.2026)
              „die sperre bei den terminen muss raus. diese wirkt sich auch
               auf den wochenplan aus. (steht ganz unten) ... entweder er ist
               gebucht oder nicht."

            „(steht ganz unten)" meint genau diese Karte. Sie ist weg, und mit
            ihr die Wege `POST /sperren` und `DELETE /sperren/:id`, die
            Schraffur im Raster, die Belegung im Terminkalender und die
            Tabelle `termin_blockung` (Migration 257).

            WAS BLEIBT: die Verfügbarkeit. Sie meldet die Firma für einen
            ganzen Tag, und das ist der Weg für Urlaub und Betriebsschulung. */}
      </div>

      {offen && (
        <Terminfenster
          x={offen} ich={ich} zu={() => setOffen(null)}
          neuLaden={laden}
          zurAkte={() => { const id = offen.auftrag_id; setOffen(null); oeffne(id); }}
        />
      )}
    </>
  );
}


